import React from 'react';
import { Tooltip } from './tooltip';

export interface LinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'type'> {
  children?: React.ReactNode;
  label?: string;
  labelClassName?: string;
  icon?: React.ReactNode;
  type?: 'underline' | 'double-underline' | 'background' | 'icon';
  intent?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'muted' | 'success-soft' | 'danger-soft';
  href?: string;
  onClick?: () => void;
  className?: string;
  title?: string;
  tooltipVariant?: 'default' | 'top' | 'bottom' | 'left' | 'right';
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(({
  children,
  label,
  labelClassName = '',
  icon,
  type = 'underline',
  intent = 'secondary',
  href,
  onClick,
  className = '',
  title,
  tooltipVariant = 'default',
  ...rest
}, ref) => {

  // Base styles for all links
  const baseStyles =
    'inline-flex items-center justify-center gap-2 text-sm font-heading font-semibold transition-all duration-200';

  // Shape / padding per type
  const typeStyles: Record<string, string> = {
    underline: 'py-2.5 border-b-2 border-transparent',
    'double-underline': 'py-2.5 border-y-2 border-transparent',
    background: 'px-4 py-2 rounded-full',
    icon: 'p-2 rounded-lg',
  };

  // Intent-based color styles with different hover effects per type
  const intentStyles: Record<string, Record<string, string>> = {
    primary: {
      underline: 'text-purple hover:border-b-2 hover:border-purple focus:ring-purple/30 focus:outline-none',
      'double-underline': 'text-purple hover:border-y-2 hover:border-purple focus:ring-purple/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-purple/5 focus:ring-purple/30 focus:outline-none',
      icon: 'text-dark-invirtus bg-purple hover:bg-purple/90 focus:ring-purple/30 focus:outline-none',
    },
    secondary: {
      underline: 'text-dark-invirtus hover:border-b-2 hover:border-dark-invirtus focus:ring-dark-invirtus/30 focus:outline-none',
      'double-underline': 'text-dark-invirtus hover:border-y-2 hover:border-dark-invirtus focus:ring-dark-invirtus/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-dark-invirtus/5 focus:ring-dark-invirtus/30 focus:outline-none',
      icon: 'text-white bg-dark-invirtus hover:bg-dark-invirtus/90 focus:ring-dark-invirtus/30 focus:outline-none',
    },
    success: {
      underline: 'text-green hover:border-b-2 hover:border-green focus:ring-green/30 focus:outline-none',
      'double-underline': 'text-green hover:border-y-2 hover:border-green focus:ring-green/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-green/5 focus:ring-green/30 focus:outline-none',
      icon: 'text-dark-invirtus bg-green hover:bg-green/90 focus:ring-green/30 focus:outline-none',
    },
    danger: {
      underline: 'text-red hover:border-b-2 hover:border-red focus:ring-red/30 focus:outline-none',
      'double-underline': 'text-red hover:border-y-2 hover:border-red focus:ring-red/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-red/5 focus:ring-red/30 focus:outline-none',
      icon: 'text-dark-invirtus bg-red hover:bg-red/90 focus:ring-red/30 focus:outline-none',
    },
    warning: {
      underline: 'text-yellow hover:border-b-2 hover:border-yellow focus:ring-yellow/30 focus:outline-none',
      'double-underline': 'text-yellow hover:border-y-2 hover:border-yellow focus:ring-yellow/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-yellow/5 focus:ring-yellow/30 focus:outline-none',
      icon: 'text-dark-invirtus bg-yellow hover:bg-yellow/90 focus:ring-yellow/30 focus:outline-none',
    },
    info: {
      underline: 'text-blue hover:border-b-2 hover:border-blue focus:ring-blue/30 focus:outline-none',
      'double-underline': 'text-blue hover:border-y-2 hover:border-blue focus:ring-blue/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-blue/5 focus:ring-blue/30 focus:outline-none',
      icon: 'text-dark-invirtus bg-blue hover:bg-blue/90 focus:ring-blue/30 focus:outline-none',
    },
    muted: {
      underline: 'text-dark-invirtus-soft hover:border-b-2 hover:border-dark-invirtus-soft focus:ring-dark-invirtus-soft/30 focus:outline-none',
      'double-underline': 'text-dark-invirtus-soft hover:border-y-2 hover:border-dark-invirtus-soft focus:ring-dark-invirtus-soft/30 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-dark-invirtus-soft/5 focus:ring-dark-invirtus-soft/30 focus:outline-none',
      icon: 'text-white bg-dark-invirtus-strong hover:bg-dark-invirtus-strong/90 focus:ring-dark-invirtus-soft/30 focus:outline-none',
    },
    'success-soft': {
      underline: 'text-dark-invirtus hover:border-b-2 hover:border-green focus:ring-green/20 focus:outline-none',
      'double-underline': 'text-dark-invirtus hover:border-y-2 hover:border-green focus:ring-green/20 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-green-soft/5 focus:ring-green/20 focus:outline-none',
      icon: 'text-dark-invirtus bg-green-soft hover:bg-green-soft/90 focus:ring-green/20 focus:outline-none',
    },
    'danger-soft': {
      underline: 'text-dark-invirtus hover:border-b-2 hover:border-red focus:ring-red/20 focus:ring-red/20 focus:outline-none',
      'double-underline': 'text-dark-invirtus hover:border-y-2 hover:border-red focus:ring-red/20 focus:outline-none',
      background: 'text-dark-invirtus hover:bg-red-soft/5 focus:ring-red/20 focus:outline-none',
      icon: 'text-dark-invirtus bg-red-soft hover:bg-red-soft/90 focus:ring-red/20 focus:outline-none',
    },
  };

  const isIconOnly = type === 'icon' || (!label && !!icon && !children);

  // Use anchor tag if href is provided, otherwise use button-like behavior
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick && !href) {
      e.preventDefault();
      onClick();
    }
  };

  const linkContent = (
    <>
      {icon}
      {!isIconOnly && label && (
        <span className={labelClassName}>{label}</span>
      )}
      {!isIconOnly && !label && children}
    </>
  );

  const linkElement = (
    <a
      ref={ref}
      href={href || (onClick ? '#' : undefined)}
      onClick={handleClick}
      className={`${baseStyles} ${typeStyles[type]} ${intentStyles[intent][type]} cursor-pointer ${className}`}
      aria-label={title || rest['aria-label']}
      {...rest}
    >
      {linkContent}
    </a>
  );

  if (title) {
    return (
      <Tooltip content={title} variant={tooltipVariant}>
        {linkElement}
      </Tooltip>
    );
  }

  return linkElement;
});

Link.displayName = 'Link';

export default Link;
